import { DateTime } from 'luxon'

import { PagedResult } from '../DTOs/Common/PagedResult'
import { PlantDTO } from '../DTOs/Plants/PlantDTO'
import { StoppageCalc } from '../DTOs/Stoppage/StoppageCalc'
import { StoppageDto } from '../DTOs/Stoppage/StoppageDto'
import { StoppageSubTypeDto } from '../DTOs/Stoppage/StoppageSubTypeDto'
import { StoppageTypeDto } from '../DTOs/Stoppage/StoppageTypeDto'
import { queryClient } from '../queryClient'
import type { TripleShift } from '../types/Shift'
import {
  appendCachedPlantStoppage,
  appendCachedStoppage,
  patchCachedOperationStatus,
} from './patchCachedStoppages'
import { stoppagesMutations } from './stoppagesMutations'

export const PLANT_JOB_TYPES_KEY = ['references', 'plantJobTypes'] as const

interface SubTypeOption {
  value: number
  label: string
  groupColor: string
  stoppageType: string
}

interface OfflineBatch {
  machineIds: number[]
  /** The machines as the modal listed them, so each row can carry its own. */
  machines?: PlantDTO[]
  stoppageTypeId: number
  stoppageSubTypeId: number
  timeIn: DateTime
  timeOut?: DateTime
  subType?: SubTypeOption
  selectedDate: DateTime
  selectedShift: TripleShift
}

function cachedPlants(): PlantDTO[] {
  return queryClient.getQueryData<PlantDTO[]>(['references', 'plantsWithDepartments']) ?? []
}

export function availableMachinesFromCache(
  selectedDate: DateTime,
  selectedShift: TripleShift,
  jobTypes?: number,
): PlantDTO[] {
  for (const [, page] of queryClient.getQueriesData<PagedResult<StoppageCalc>>({
    queryKey: ['dataTable', 'operations', 'stoppageCalcs', selectedDate.toISODate(), selectedShift],
  })) {
    const rows = page?.data ?? []
    if (!rows.length) continue

    const plants = cachedPlants()

    return rows
      .filter(row => row.status === 'UP' && row.plantId)
      .map(row => plants.find(plant => plant.id === row.plantId)
        ?? ({ id: row.plantId, plantRef: row.machineName } as PlantDTO))
      .filter(plant => !jobTypes || plant.jobTypes == null || (plant.jobTypes & jobTypes) !== 0)
  }
  return []
}

export async function addMultipleStoppagesOffline(batch: OfflineBatch): Promise<number> {
  const dateIso = batch.selectedDate.toISODate()!
  let added = 0

  for (const plantId of batch.machineIds) {
    const response = await stoppagesMutations.addStoppage(new StoppageDto({
      plantId,
      startTime: batch.timeIn,
      endTime: batch.timeOut,
      stoppageTypeId: batch.stoppageTypeId,
      stoppageSubTypeId: batch.stoppageSubTypeId,
      incident: false,
    }))

    if (!response.data) continue

    const row = new StoppageDto({
      ...response.data,
      plant: (batch.machines?.find(machine => machine.id === plantId)
        ?? { id: plantId } as PlantDTO) as StoppageDto['plant'],
      stoppageSubType: new StoppageSubTypeDto({
        stoppageSubTypeId: batch.stoppageSubTypeId,
        stoppageSubType: batch.subType?.label ?? '',
        stoppageTypeId: batch.stoppageTypeId,
        departmentOwnerId: 0,
      }),
      stoppageType: new StoppageTypeDto({
        stoppageTypeId: batch.stoppageTypeId,
        stoppageType: batch.subType?.stoppageType ?? '',
        colourHex: batch.subType?.groupColor,
      }),
    })

    appendCachedStoppage(row, dateIso, batch.selectedShift)
    appendCachedPlantStoppage(row, dateIso, batch.selectedShift, plantId)

    if (!batch.timeOut) {
      patchCachedOperationStatus(plantId, dateIso, batch.selectedShift, {
        status: batch.subType?.stoppageType ?? 'Maintenance Down',
        statusDescription: `${batch.subType?.stoppageType ?? ''} - ${batch.subType?.label ?? ''}`.trim(),
        stoppageColourHub: batch.subType?.groupColor,
        stoppageId: response.data.stoppageId,
        currentStoppageTypeId: batch.stoppageTypeId,
        currentStoppageSubTypeId: batch.stoppageSubTypeId,
        timeIn: batch.timeIn,
      })
    }

    added++
  }

  return added
}
